import { computed, ref } from 'vue'
import usePostApi from '@/composables/post/postApi'
import usePostList, { PostsRoot } from '@/composables/post/postList'
import useAppState from '@/composables/ui/ui'
import { Post } from '@/model/data-object/Post'
import useConfiguration from '@/composables/configuration/configuration'
import useProfileMenu, { ProfileMenu } from '@/composables/profile/profileMenu'
import { useProfileShow } from '@/composables/user/user'
import usePostParentManager from '@/composables/post/postParentManager'

const totalCount = ref(0)
const {
  postList,
  postTree,
  addToList,
  getLastId,
  clearListData: clearList,
} = usePostList()

const { loadingList } = useAppState()
const hasNext = ref(true)

export default function useUserPostLoader() {
  const { activePage } = useProfileMenu()
  const { configuration } = useConfiguration()
  const { isShowingCurrentUser } = useProfileShow()
  const loadedCount = computed(() => postList.length)
  const {
    resolvePostParents,
    resolveMissingParentStack,
  } = usePostParentManager()

  const {
    fetchMyHidden: fetchMyHiddenApi,
    fetchMyVisible: fetchMyVisibleApi,
    fetchByAuthor: fetchByAuthorApi,
    fetchMyUnseenReplies: fetchMyUnseenRepliesApi,
    fetchMyAllReplies: fetchMyAllRepliesApi,
  } = usePostApi()

  const fetchHidden = () => {
    return fetchMyHiddenApi(getLastId(), undefined)
  }

  const fetchUnseenReplies = () => {
    return fetchMyUnseenRepliesApi(undefined)
  }

  const fetchAllReplies = () => {
    return fetchMyAllRepliesApi(getLastId(), undefined)
  }

  const fetchPosts = () => {
    if (isShowingCurrentUser.value) {
      return fetchMyVisibleApi(getLastId(), undefined)
    }

    return fetchByAuthorApi(getLastId(), undefined)
  }

  const fetchNext = () => {
    if (activePage.value === ProfileMenu.ALL_POSTS) {
      return fetchPosts()
    }
    if (activePage.value === ProfileMenu.HIDDEN_POSTS) {
      return fetchHidden()
    }
    if (activePage.value === ProfileMenu.UNSEEN_REPLIES_POSTS) {
      return fetchUnseenReplies()
    }
    if (activePage.value === ProfileMenu.ALL_REPLIES_POSTS) {
      return fetchAllReplies()
    }

    return fetchPosts()
  }

  const loadMissingParents = async (postRootList: Array<PostsRoot>) => {
    if (
      [
        ProfileMenu.UNSEEN_REPLIES_POSTS,
        ProfileMenu.ALL_REPLIES_POSTS,
      ].includes(activePage.value)
    ) {
      resolvePostParents(postRootList)
      await resolveMissingParentStack()
    }
  }

  const loadNext = async () => {
    const { response, request, count } = fetchNext()

    loadingList.value = true
    await request()
    if (response.value) {
      const newPostList = addToList(response.value as Post[])
      totalCount.value = count.value
      if (response.value.length < configuration.pageSize) {
        hasNext.value = false
      }

      await loadMissingParents(newPostList)
    }

    loadingList.value = false
  }

  function clearListData() {
    totalCount.value = 0
    hasNext.value = true
    clearList()
  }

  return {
    postTree,
    postList,
    loadingList,
    hasNext,
    loadedCount,
    totalCount,
    clearListData,
    loadNext,
  }
}
